"use client";

import { useMemo } from "react";
import { listingHref } from "@shared/routing/listing-href";
import { ListingsClusterMapClient } from "@/components/location/listings-cluster-map-client";
import type { ClusterListing } from "@/components/location/listings-cluster-map";
import { US_MAP_CENTER } from "@/lib/maps/map-defaults";
import { useHeroMapContext } from "@/components/home/context/hero-map-context";
import { getZoomForRadius } from "@/components/home/map/map-styles";
import type { BrowseListing } from "@/components/listing/browse/marketplace-listing";
import { formatNumber } from "@/lib/formatting/numbers";

const NATIONWIDE_ZOOM = 4;
const PREVIEW_MAX_ZOOM = 11;
const MERCATOR_LATITUDE_SPAN = 170;

export function useClusterListings(
  products: Array<BrowseListing>,
  requestedQuantity?: number,
): ClusterListing[] {
  return useMemo(
    () =>
      products.flatMap((product) => {
        const { cityLatitude, cityLongitude } = product;
        if (
          typeof cityLatitude !== "number" ||
          !Number.isFinite(cityLatitude) ||
          typeof cityLongitude !== "number" ||
          !Number.isFinite(cityLongitude)
        ) {
          return [];
        }
        return [
          {
            id: product._id,
            title: product.title,
            slug: product.slug,
            productTypeSlug: product.productTypeSlug,
            buyerUnitPriceCents: product.buyerUnitPriceCents,
            cityName: product.cityName,
            stateAbbr: product.stateAbbr,
            cityLatitude,
            cityLongitude,
            primaryImageUrl: product.primaryImageUrl,
            href: listingHref(product, requestedQuantity),
          },
        ];
      }),
    [products, requestedQuantity],
  );
}

function boundsZoom(latSpan: number, lngSpan: number): number {
  if (latSpan < 0.01 && lngSpan < 0.01) return PREVIEW_MAX_ZOOM;
  const latZoom = Math.log2(MERCATOR_LATITUDE_SPAN / Math.max(latSpan, 0.01));
  const lngZoom = Math.log2(360 / Math.max(lngSpan, 0.01));
  return Math.max(
    NATIONWIDE_ZOOM,
    Math.min(PREVIEW_MAX_ZOOM, Math.floor(Math.min(latZoom, lngZoom))),
  );
}

export function previewViewport(listings: ClusterListing[]) {
  if (listings.length === 0) return null;
  const latitudes = listings.map((listing) => listing.cityLatitude);
  const longitudes = listings.map((listing) => listing.cityLongitude);
  const minLat = Math.min(...latitudes);
  const maxLat = Math.max(...latitudes);
  const minLng = Math.min(...longitudes);
  const maxLng = Math.max(...longitudes);
  return {
    center: {
      latitude: (minLat + maxLat) / 2,
      longitude: (minLng + maxLng) / 2,
    },
    zoom: boundsZoom(maxLat - minLat, maxLng - minLng),
  };
}

export interface MapPageScope {
  page: number;
  pageSize: number;
  loadedCount: number;
  totalCount: number | null;
  hasNextPage: boolean;
}

export function mapScopeNote({
  page,
  pageSize,
  loadedCount,
  totalCount,
  hasNextPage,
}: MapPageScope): string | null {
  if (loadedCount === 0) return null;
  const isWholeResult =
    page === 1 && !hasNextPage && (totalCount === null || totalCount <= loadedCount);
  if (isWholeResult) return null;
  const first = (page - 1) * pageSize + 1;
  const last = first + loadedCount - 1;
  const range = `${formatNumber(first)}–${formatNumber(last)}`;
  if (totalCount === null) return `Map shows listings ${range}, this page only`;
  return `Map shows listings ${range} of ${formatNumber(totalCount)}`;
}

export function ListingMapPane({
  listings,
  scopeNote = null,
  className,
}: {
  listings: ClusterListing[];
  scopeNote?: string | null;
  className?: string;
}) {
  const { location, radiusMiles, hoveredProductId, setHoveredProductId } =
    useHeroMapContext();
  const viewport = useMemo(() => previewViewport(listings), [listings]);

  const map = (
    <ListingsClusterMapClient
      listings={listings}
      className={className ?? "h-full w-full"}
      mapClassName="h-full w-full rounded-none border-0"
      defaultZoom={
        viewport?.zoom ??
        (location ? getZoomForRadius(radiusMiles ?? 200) : NATIONWIDE_ZOOM)
      }
      defaultCenter={
        viewport?.center ??
        (location
          ? { latitude: location.lat, longitude: location.lng }
          : { latitude: US_MAP_CENTER.lat, longitude: US_MAP_CENTER.lng })
      }
      highlightedId={hoveredProductId}
      onHoverListing={setHoveredProductId}
      emptyMessage="No listings to map for these filters."
    />
  );
  if (!scopeNote || listings.length === 0) return map;
  return (
    <div className="relative h-full w-full">
      {map}
      <p
        role="status"
        className="pointer-events-none absolute inset-x-0 top-3 z-10 mx-auto w-fit max-w-[calc(100%-1.5rem)] rounded-full bg-surface px-3 py-1.5 text-center text-caption font-medium text-foreground shadow-card"
      >
        {scopeNote}
      </p>
    </div>
  );
}
